/**
 * Part of what changed, as the next version (§5.25): the document a publish
 * sends when a person ticks some of the lines of its diff and leaves the rest
 * on their machine.
 *
 * It walks the two versions exactly as `diff.ts` does, so every line of a diff
 * is one decision here and is named the same way — `repos[api].startCmd`,
 * `repos[api].api.requests[Login]`. A line that is chosen takes this copy's
 * value; one that is not keeps the published one: an addition left out is not
 * added, a removal left out keeps the item, a change left out keeps the old
 * value. Lists of things (`shape.ts`) come out in this copy's order, with the
 * items a removal left behind at the end.
 *
 * What comes out is a document like any other and is parsed before it is sent:
 * a choice that breaks one — a call ticked without the new folder it is filed
 * in, a folder of a repository whose repository was left out — is refused by
 * name there rather than guessed at here.
 */
import type { WorkspaceDocument } from './document';
import { LISTS, indexed, isPlainObject, labels, sameValue } from './shape';

export function pickChanges(
  before: WorkspaceDocument | null,
  after: WorkspaceDocument,
  chosen: ReadonlySet<string>
): WorkspaceDocument {
  // A first version has nothing to keep: its diff is one line, the whole of it.
  if (!before) return after;
  return pick('', '', before, after, chosen) as WorkspaceDocument;
}

function pick(shapePath: string, path: string, a: unknown, b: unknown, chosen: ReadonlySet<string>): unknown {
  if (sameValue(a, b)) return b;
  const shape = LISTS[shapePath];
  if (shape && (Array.isArray(a) || a === undefined) && (Array.isArray(b) || b === undefined)) {
    const left = indexed(shape, a);
    const right = indexed(shape, b);
    const names = labels(shape, (a as unknown[]) ?? [], (b as unknown[]) ?? []);
    const out: unknown[] = [];
    for (const [key, item] of right) {
      const at = `${path}[${names.get(key)}]`;
      const previous = left.get(key);
      if (!previous) {
        if (chosen.has(at)) out.push(item);
      } else out.push(pick(shapePath, at, previous, item, chosen));
    }
    for (const [key, item] of left) {
      if (!right.has(key) && !chosen.has(`${path}[${names.get(key)}]`)) out.push(item);
    }
    return a === undefined && !out.length ? undefined : out;
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const out: Record<string, unknown> = {};
    for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
      const value = pick(shapePath ? `${shapePath}.${key}` : key, path ? `${path}.${key}` : key, a[key], b[key], chosen);
      if (value !== undefined) out[key] = value;
    }
    return out;
  }
  // One line of the diff: added, removed or changed at `path`.
  return chosen.has(path) ? b : a;
}
